import { __ } from '@wordpress/i18n';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, {
  useEffect,
  useState,
} from 'react';

// Components.
import SafeHTML from '../safe-html';

// Services.
import formatDate from '../../js/services/formatDate';
import getApiFetch from '../../js/services/getApiFetch';

// Utils.
import getPostThumbnail from '../../js/utils/getPostThumbnail';

/**
 * A component to handle the display of the Content Type List feature.
 * @param {array} postTypes - An array of objects containing information about post types to display.
 * @param {number} stepping - The number of posts to show at once.
 * @param {string} topicSlug - The slug of the topic, extracted from the URL.
 * @returns {object} - JSX for this component.
 */
const ContentTypeList = ({
  postTypes,
  stepping,
  topicSlug,
}) => {
  // Get a pre-initialized copy of the apiFetch function with the REST base already set.
  const apiFetch = getApiFetch();

  // Set up getters and setters for state fields.
  const [filters, setFilters] = useState([]);
  const [filteredPosts, setFilteredPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [multiplier, setMultiplier] = useState(1);
  const [posts, setPosts] = useState([]);
  const [postType, setPostType] = useState('');
  const [topic, setTopic] = useState({});

  // Load information about the topic from the REST API.
  useEffect(
    () => {
      apiFetch({
        path: `/wp/v2/topics?slug=${topicSlug}`,
      })
        .then((topics) => {
          if (Array.isArray(topics) && 1 === topics.length) {
            setTopic(topics[0]);
          }
        });
    },
    [topicSlug]
  );

  // Load content from the REST API.
  useEffect(
    () => {
      const {
        id = 0,
      } = topic;

      // If we don't have a topic ID, bail.
      if (0 === id) {
        return;
      }

      // Loop over post types (other than 'All') and load first 100 posts for each type that match the current topic.
      const loaders = postTypes
        .filter(({ slug }) => '' !== slug)
        .map(({ slug }) => apiFetch({
          path: `/wp/v2/${slug}?topics=${id}&_embed&posts_per_page=100`,
        }));

      // Once all posts have loaded, add them to state and turn off the loading flag.
      Promise.all(loaders)
        .then((data) => {
          const allPosts = data.reduce((acc, item) => [...acc, ...item], []);
          setPosts(allPosts);

          // Initialize the list of filtered posts to all posts.
          setFilteredPosts([...allPosts]);

          // Set the filters based on whether post types are filterable and have content.
          setFilters(postTypes.filter((postTypeTest) =>
            postTypeTest.filterable && (
              '' === postTypeTest.type ||
              0 < allPosts.filter((post) =>
                postTypeTest.type === post.type).length)));

          // Tell the app that we are no longer loading data.
          setLoading(false);
        });
    },
    [topic]
  );

  // Update the list of filtered posts when the post type changes.
  useEffect(
    () => {
      setFilteredPosts(posts.filter((post) => (
        '' === postType
        || postType === post.type)));
    },
    [postType]
  );

  // Handle loading state.
  if (loading) {
    return (
      <div className="content-listing">
        <div className="content-listing__loading">
          {__('Loading...', 'knight-foundation-app')}
        </div>
      </div>
    );
  }

  // Compute visible post count.
  const postCount = stepping * multiplier;

  // Display the component.
  return (
    <div className="content-listing">
      <h2>
        {__('Explore', 'knight-foundation-app')}
        {' '}
        {topic.name}
      </h2>
      <div className="content-listing__panel">
        <div className="content-listing__toolbar">
          <ul className="content-listing__toolbar-inner">
            {filters.map(({ label, type }) => (
              <li key={type}>
                <button
                  className={classNames(
                    'content-listing__filter-item',
                    {
                      'is-active': postType === type,
                    }
                  )}
                  onClick={() => {
                    setMultiplier(1);
                    setPostType(type);
                  }}
                >
                  {label}
                </button>
              </li>
            ))}
          </ul>
        </div>
        <div className="content-listing__panel-content">
          <ul className="content-listing__content-list">
            {filteredPosts
              .sort((p1, p2) => p2.date.localeCompare(p1.date))
              .slice(0, postCount).map((post) => {
                const {
                  date = '',
                  id = 0,
                  link = '',
                  title: {
                    rendered: title = '',
                  } = {},
                  topics = [],
                } = post;

                // Ensure there is, at minimum, a title a link, and a topic.
                if (! link || ! title || ! topics.includes(topic.id)) {
                  return null;
                }

                // Get thumbnail for image. Function will return empty string or full url if nothing exists.
                const thumbnail = getPostThumbnail(post);

                // Attempt to get a date object from the date.
                const dateFormatted = formatDate(new Date(date), {
                  dateStyle: 'long',
                });

                return (
                  <li
                    className="content-listing__item"
                    key={id}
                  >
                    {thumbnail && (
                      <div className="item__image">
                        <img
                          alt=""
                          src={thumbnail}
                        />
                      </div>
                    )}
                    <div className="item__details">
                      <a
                        className="item__cover"
                        href={link}
                      >
                        <SafeHTML
                          className="item__title"
                          html={title}
                          tag="div"
                        />
                      </a>
                      {'' !== dateFormatted && (
                        <div className="item__date">
                          {dateFormatted}
                        </div>
                      )}
                    </div>
                  </li>
                );
              })}
          </ul>
          {postCount < filteredPosts.length && (
            <div className="content-listing__more">
              <button onClick={() => setMultiplier(multiplier + 1)}>
                {__('Load More', 'knight-foundation-app')}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Define PropTypes for this component.
ContentTypeList.propTypes = {
  postTypes: PropTypes.arrayOf(PropTypes.shape({
    filterable: PropTypes.bool.isRequired,
    label: PropTypes.string.isRequired,
    slug: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
  })).isRequired,
  stepping: PropTypes.number.isRequired,
  topicSlug: PropTypes.string.isRequired,
};

export default ContentTypeList;
